import { supabase } from './supabase';

const BASE = import.meta.env.VITE_API_URL || '/api';

/**
 * Common API client against topStudio Worker.
 * Auto-injects Supabase JWT as Bearer header.
 * Field names match backend (snake_case).
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = new Headers(init?.headers);
  if (!headers.has('Content-Type') && init?.body) {
    headers.set('Content-Type', 'application/json');
  }
  // Skicka Bearer om vi har en explicit Supabase-session i frontend.
  // Annars: Worker faller tillbaka till th_sso-cookien som hq/portal satt.
  if (session?.access_token) {
    headers.set('Authorization', `Bearer ${session.access_token}`);
  }
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    // include = skicka cookies aven for cross-origin (vi ar same-origin men explicit OK).
    // Behovs sa th_sso-cookien folger med pa varje API-request.
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text || res.statusText);
  }
  return res.json();
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export const api = {
  projects: {
    list: () => request<{ projects: Project[] }>('/projects'),
    create: (name: string, description?: string) =>
      request<Project>('/projects', {
        method: 'POST',
        body: JSON.stringify({ name, description }),
      }),
    get: (id: string) => request<Project>(`/projects/${id}`),
    listEntities: (projectId: string) =>
      request<{ entities: ProjectEntity[] }>(`/projects/${projectId}/entities`),
    listAssets: (projectId: string) =>
      request<{ assets: Asset[] }>(`/projects/${projectId}/assets`),
    update: (projectId: string, fields: { name?: string; description?: string; state?: unknown }) =>
      request<Project>(`/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify(fields),
      }),
  },
  assets: {
    upload: async (projectId: string, file: File, kind?: 'video' | 'image' | 'audio') => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new ApiError(401, 'no_session');
      const res = await fetch(`${BASE}/projects/${projectId}/assets`, {
        method: 'POST',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'Authorization': `Bearer ${session.access_token}`,
          'X-Original-Name': file.name,
          ...(kind ? { 'X-Asset-Kind': kind } : {}),
        },
        body: file,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new ApiError(res.status, text || res.statusText);
      }
      return res.json() as Promise<Asset>;
    },
  },
  entities: {
    update: (entityId: string, fields: { user_label?: string; display_name?: string }) =>
      request<Entity>(`/entities/${entityId}`, {
        method: 'PATCH',
        body: JSON.stringify(fields),
      }),
    listAppearances: (entityId: string) =>
      request<{ appearances: EntityAppearance[] }>(`/entities/${entityId}/appearances`),
  },
  scenes: {
    list: (assetId: string) => request<{ scenes: Scene[] }>(`/assets/${assetId}/scenes`),
  },
  transcripts: {
    list: (assetId: string) =>
      request<{ transcripts: Transcript[] }>(`/assets/${assetId}/transcripts`),
    search: (q: string) =>
      request<{ matches: Transcript[] }>(`/transcripts/search?q=${encodeURIComponent(q)}`),
  },
  preferences: {
    get: () => request<{ preferences: UserPreferences; updated_at: string | null }>(`/preferences`),
    patch: (preferences: Partial<UserPreferences>) =>
      request<{ user_id: string; preferences: UserPreferences }>(`/preferences`, {
        method: 'PATCH',
        body: JSON.stringify({ preferences }),
      }),
  },
  ai: {
    analyze: (project_id: string, r2_key: string) =>
      request<JobAccepted>('/ai/analyze', {
        method: 'POST',
        body: JSON.stringify({ project_id, r2_key }),
      }),
    segment: (project_id: string, asset_id: string, point_xy: [number, number]) =>
      request<JobAccepted>('/ai/segment', {
        method: 'POST',
        body: JSON.stringify({ project_id, asset_id, point_xy }),
      }),
    upscale: (project_id: string, asset_id: string, factor: 2 | 3 | 4) =>
      request<JobAccepted>('/ai/upscale', {
        method: 'POST',
        body: JSON.stringify({ project_id, asset_id, factor }),
      }),
    rembg: (project_id: string, asset_id: string) =>
      request<JobAccepted>('/ai/rembg', {
        method: 'POST',
        body: JSON.stringify({ project_id, asset_id }),
      }),
    whisper: (project_id: string, asset_id: string, language?: string) =>
      request<JobAccepted>('/ai/whisper', {
        method: 'POST',
        body: JSON.stringify({ project_id, asset_id, language }),
      }),
    comfyui: (project_id: string, prompt: string, style: string, kind: 'image' | 'video') =>
      request<JobAccepted>('/ai/comfyui', {
        method: 'POST',
        body: JSON.stringify({ project_id, prompt, style, kind }),
      }),
  },
};

// ====== Types ======

export type Project = {
  id: string;
  name: string;
  description: string | null;
  owner_id: string;
  thumbnail_key: string | null;
  state?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type Asset = {
  id: string;
  project_id: string;
  kind: 'video' | 'image' | 'audio' | 'font' | 'ai-generated';
  r2_key: string;
  original_name: string | null;
  mime_type: string | null;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
};

export type EntityKind =
  | 'face'
  | 'person'
  | 'sky'
  | 'sun'
  | 'mountain'
  | 'vehicle'
  | 'object'
  | 'title'
  | 'audio_voice'
  | 'audio_music'
  | 'background';

export type Entity = {
  id: string;
  project_id: string;
  kind: EntityKind;
  display_name: string | null;
  user_label: string | null;
  thumbnail_r2_key: string | null;
  occurrences: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type ProjectEntity = Pick<
  Entity,
  'kind' | 'display_name' | 'user_label' | 'thumbnail_r2_key' | 'occurrences'
> & {
  project_id: string;
  entity_id: string;
  appearance_count: number;
  asset_ids: string[] | null;
  created_at: string;
};

export type EntityAppearance = {
  id: string;
  entity_id: string;
  asset_id: string;
  scene_index: number | null;
  start_ms: number;
  end_ms: number;
  bbox_keyframes: Array<{ t_ms: number; x: number; y: number; w: number; h: number }> | null;
  mask_r2_key: string | null;
  confidence: number | null;
};

export type Scene = {
  id: string;
  asset_id: string;
  scene_index: number;
  start_ms: number;
  end_ms: number;
  thumbnail_r2_key: string | null;
  shot_type: 'wide' | 'medium' | 'close-up' | 'extreme-close-up' | null;
  ai_summary: string | null;
};

export type Transcript = {
  id: string;
  asset_id: string;
  language: string;
  full_text: string;
  speaker_count: number | null;
  raw_segments_r2_key: string | null;
  generated_at: string;
};

export type UserPreferences = {
  default_aspect_ratio?: '16:9' | '9:16' | '1:1' | '4:5';
  default_style_preset?: string;
  last_used_tools?: string[];
  voice_input_lang?: string;
  ui_density?: 'compact' | 'comfortable';
  [k: string]: unknown;
};

export type JobAccepted = { job_id: string; status: 'pending' | 'running' | 'success' | 'failed' };
